import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import useAuth from "../../hooks/useAuth";
import "../AdminDashboardPage/AdminDashboardPage.css";

const API = (process.env.REACT_APP_API_URL || "") + "/api/receipts";

const displayAccountName = name => {
  if (!name) return name;
  const idx = name.lastIndexOf(':');
  return idx >= 0 ? name.slice(idx + 1).trim() : name;
};

export default function MissingReceiptsPage() {
  const [user, token] = useAuth();
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ownerFilter, setOwnerFilter] = useState('all'); // 'all' | 'unassigned' | userId
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    const url = isAdmin
      ? `${API}/admin/missing-receipts/${ownerFilter !== 'all' ? `?user_id=${ownerFilter}` : ''}`
      : `${API}/my-missing-receipts/`;
    axios.get(url, authHeader)
      .then(res => setRows(res.data))
      .catch(() => setError("Couldn't load missing receipts. Try refreshing."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [isAdmin, ownerFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const owners = useMemo(() => {
    const seen = new Map();
    rows.forEach(r => {
      if (r.resolved_user && !seen.has(r.resolved_user)) {
        seen.set(r.resolved_user, r.resolved_user_name || `User #${r.resolved_user}`);
      }
    });
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const syncNow = () => {
    setSyncing(true);
    setSyncMsg("");
    axios.post(`${API}/admin/missing-receipts/sync/`, {}, authHeader)
      .then(() => { setSyncMsg("Synced from QuickBooks."); load(); })
      .catch(err => setSyncMsg(err.response?.data?.error || "Sync failed."))
      .finally(() => setSyncing(false));
  };

  const uploadReceipt = (row) => {
    navigate("/submit", {
      state: {
        prelink: {
          purchase_id: row.qbo_purchase_id,
          vendor_qbo_id: row.vendor_qbo_id,
          vendor_name: row.vendor_name,
          txn_date: row.txn_date,
          total_amount: row.total_amount,
          payment_account_qbo_id: row.payment_account_qbo_id,
          payment_account_name: row.payment_account_name,
          card_last_four: row.card_last_four,
          class_name: row.class_name,
          bank_description: row.bank_description,
        },
      },
    });
  };

  return (
    <div className="admin-page">
      <h1>Missing Receipts</h1>

      <div className="alert alert-info" style={{ marginBottom: "1.25rem" }}>
        <strong>Under Construction</strong> — This page is currently being developed.
      </div>

      {isAdmin && (
        <div className="card mb-2" style={{ display: "flex", flexWrap: "wrap", gap: "1.25rem", alignItems: "center" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.9rem", alignItems: "center" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", cursor: "pointer", fontSize: "0.9rem" }}>
              <input type="radio" name="owner-filter" checked={ownerFilter === 'all'} onChange={() => setOwnerFilter('all')} />
              All
            </label>
            {owners.map(o => (
              <label key={o.id} style={{ display: "flex", alignItems: "center", gap: "0.35rem", cursor: "pointer", fontSize: "0.9rem" }}>
                <input type="radio" name="owner-filter" checked={String(ownerFilter) === String(o.id)} onChange={() => setOwnerFilter(o.id)} />
                {o.name}
              </label>
            ))}
            <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", cursor: "pointer", fontSize: "0.9rem" }}>
              <input type="radio" name="owner-filter" checked={ownerFilter === 'unassigned'} onChange={() => setOwnerFilter('unassigned')} />
              Unassigned
            </label>
          </div>
          <button className="btn-secondary" onClick={syncNow} disabled={syncing} style={{ marginLeft: "auto" }}>
            {syncing ? "Syncing…" : "Sync Now"}
          </button>
          {syncMsg && <span className="muted" style={{ fontSize: "0.85rem" }}>{syncMsg}</span>}
        </div>
      )}

      {loading && <p>Loading...</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {!loading && !error && rows.length === 0 && (
        <p className="empty">
          {isAdmin ? "No outstanding missing receipts." : "You're all caught up — no missing receipts."}
        </p>
      )}

      {rows.length > 0 && (
        <>
          {/* Mobile cards */}
          <div className="mobile-card-list">
            {rows.map(r => (
              <div key={r.id} className="mobile-card">
                <div className="mobile-card-row">
                  <div>
                    <div className="mobile-card-vendor">{r.vendor_name || <span className="muted">Unknown vendor</span>}</div>
                    <div className="mobile-card-meta">
                      {r.txn_date} · {r.card_last_four ? `•••• ${r.card_last_four}` : displayAccountName(r.payment_account_name)}
                      {isAdmin && r.resolved_user_name && <> · {r.resolved_user_name}</>}
                      {isAdmin && !r.resolved_user_name && <> · <span className="muted">Unassigned</span></>}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div className="mobile-card-amount">${parseFloat(r.total_amount || 0).toFixed(2)}</div>
                  </div>
                </div>
                <div className="mobile-card-actions">
                  {r.already_submitted
                    ? <span className="status-badge status-submitted">Pending Review</span>
                    : <button className="btn-action" onClick={() => uploadReceipt(r)}>Upload Receipt</button>}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <table className="data-table desktop-only">
            <thead>
              <tr>
                <th>Date</th>
                <th>Vendor</th>
                <th>Amount</th>
                <th>Card / Account</th>
                {isAdmin && <th>Owner</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td>{r.txn_date}</td>
                  <td>{r.vendor_name || <span className="muted">—</span>}</td>
                  <td>${parseFloat(r.total_amount || 0).toFixed(2)}</td>
                  <td>{r.card_last_four ? `•••• ${r.card_last_four}` : displayAccountName(r.payment_account_name)}</td>
                  {isAdmin && (
                    <td>
                      {r.resolved_user_name || <span className="muted">Unassigned</span>}
                    </td>
                  )}
                  <td>
                    {r.already_submitted
                      ? <span className="status-badge status-submitted">Pending Review</span>
                      : <button className="btn-action" onClick={() => uploadReceipt(r)}>Upload Receipt</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
